import React, { useState } from 'react';
import { Button, ButtonProps } from 'antd';
import { useApiLoading } from '../../hooks/useApiLoading';

type LoadingButtonProps = ButtonProps & {
  useGlobalApiLoading?: boolean;
  autoLoading?: boolean;
};

/**
 * Drop-in replacement for AntD Button that shows a loader when:
 * - global API requests are in-flight (useGlobalApiLoading=true), or
 * - its own async onClick is pending (autoLoading=true).
 */
export const LoadingButton: React.FC<LoadingButtonProps> = ({
  useGlobalApiLoading = true,
  autoLoading = true,
  onClick,
  loading,
  ...rest
}) => {
  const globalLoading = useApiLoading();
  const [localLoading, setLocalLoading] = useState(false);

  const isPromiseLike = (value: unknown): value is Promise<unknown> =>
    !!value && typeof (value as any).then === 'function';

  const handleClick: React.MouseEventHandler<HTMLElement> = async (e) => {
    if (!onClick) return;
    const maybePromise = onClick(e) as unknown;

    if (autoLoading && isPromiseLike(maybePromise)) {
      try {
        setLocalLoading(true);
        await maybePromise;
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const computedLoading =
    loading !== undefined
      ? loading
      : (useGlobalApiLoading ? globalLoading : false) || localLoading;

  return <Button {...rest} onClick={handleClick} loading={computedLoading} />;
};

export default LoadingButton;

